import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc, increment, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import CommentsOffcanvas from '../component/CommentsOffcanvas';
import SEO from '../component/SEO';
import { formatCreatedAt, formatTimeAgo, getReadTime } from '../utils/Universalfunctions';
import CommentsList from '../component/CommentsList';
import Loading from '../component/Loading';
import { useAuth } from '../config/AuthContext';
import { Instagram, EditNote } from '@mui/icons-material';
import ImageGenerateAds from '../Ads/ImageGenerateAds';
import EditProblemOffcanvas from '../component/EditProblemOffcanvas';

const InterestedDetail = () => {
     const { id } = useParams();
     const { user } = useAuth();
     const [problemData, setProblemData] = useState(null);
     const [loading, setLoading] = useState(true);
     const [errorText, setErrorText] = useState('');
     const [showComments, setShowComments] = useState(false);
     const [showEdit, setShowEdit] = useState(false);





     useEffect(() => {
          const fetchProblemDetail = async () => {
               try {
                    setLoading(true);
                    setErrorText('');

                    const docRef = doc(db, 'problems', id);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                         setProblemData({
                              id: docSnap.id,
                              ...docSnap.data(),
                         });
                    } else {
                         setErrorText('Problem not found.');
                    }
               } catch (error) {
                    console.error('Problem detail fetch error:', error);
                    setErrorText('Failed to load problem details.');
               } finally {
                    setLoading(false);
               }
          };

          if (id) {
               fetchProblemDetail();
          }
     }, [id]);



     useEffect(() => {
          const incrementInterest = async () => {
               try {


                    if (!problemData || !user?.uid) return;

                    // ❌ prevent owner self-increment
                    if (problemData?.uid === user?.uid) return;

                    const ref = doc(db, 'problems', id);

                    await updateDoc(ref, {
                         interestedCount: increment(1)
                    });

               } catch (err) {
                    console.error('Auto interested error:', err);
               }
          };

          if (id && user?.uid && problemData) {
               incrementInterest();
          }

     }, [id, user?.uid, problemData]);




     if (loading) {
          return (
               <div className="immersive-detail text-center">
                    <Loading text="Loading..." />
               </div>
          );
     }

     if (errorText || !problemData) {
          return (
                <div className="immersive-detail text-center">
                    <Loading text={errorText || 'Problem not found.'} />
               </div>
          );
     }


     const {
          category,
          problem,
          userName,
          visibility,
          likes,
          comments,
          createdAt,
          interestedCount,
          content,
          type,
          title,
          isEdited,
          editedAt,
          bookmarkCount
     } = problemData;



     return (
          <>

               <SEO
                    title={problemData?.problem || "Idea Details"}
                    description={problemData?.problem?.slice(0, 150)}
               />

               <div className="immersive-detail">
                    <div className="story-container">
                         <div className="story-header">
                              <div className="meta-strip">
                                   <Link className='pill' to="/"> Home </Link>
                                   <span className="pill">
                                        Date / 2026
                                   </span>
                                   <span className="read-time">
                                        {getReadTime(problem)}
                                   </span>
                              </div>

                              <div className="author-row">
                                   <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(userName || 'U')}&background=111827&color=fff`} alt={userName || 'User'} />
                                   <div className="author-meta">
                                        <strong>{userName || 'Unknown User'}</strong>
                                        <span>
                                             {visibility || 'Public'} • {formatTimeAgo(createdAt)}
                                        </span>
                                   </div>
                                   {user?.uid === problemData?.uid && (
                                        <button
                                             onClick={() => setShowEdit(true)}
                                             className="edit-btn ms-auto"
                                        >
                                             <EditNote /> Edit
                                        </button>
                                   )}
                              </div>

                         </div>

                         <section className="metrics-bar">
                              <div className="metric">
                                   <label>Likes</label>
                                   <strong>{likes || 0}</strong>
                              </div>
                              <div className="metric">
                                   <label>Comments</label>
                                   <strong>{comments || 0}</strong>
                              </div>

                              <div className="metric">
                                   <label>Interested</label>
                                   <strong>{interestedCount || 0}</strong>
                              </div>

                              <div className="metric">
                                   <label>Bookmarks</label>
                                   <strong>{bookmarkCount || 0}</strong>
                              </div>

                              <div className="metric">
                                   <label>type</label>
                                   <strong className={`high ${type === 'Problem' ? 'text-danger' : 'text-success'}`}>{type}</strong>
                              </div>
                         </section>

                         <article className="story-content">



                              <section className="content-block">
                                   <div className='cb-cates'>
                                        <h6> Priority: {category}</h6>
                                        {isEdited && <>
                                             <h6> {isEdited}</h6>
                                             <h6> Edit Date: {formatCreatedAt(editedAt)}</h6>
                                        </>
                                        }
                                   </div>

                                   <h3>{title}</h3>
                                   <p>{problem}</p>

                                   <div style={{ margin: '40px 0'}}>
                                        <ImageGenerateAds />
                                   </div>


                                   <div
                                        dangerouslySetInnerHTML={{ __html: content }}
                                   />

                                   {problemData?.instagram && (
                                        <Link
                                             to={`https://www.instagram.com/${problemData.instagram}`}
                                             target="_blank"
                                             rel="noopener noreferrer"
                                             className="insta-link">
                                             <span className="icon">
                                                  <Instagram />
                                             </span>
                                             <span className="label">Instagram</span>
                                        </Link>
                                   )}
                              </section>

                              <div className="section-divider">
                                   <div className="line"></div>
                                   <div className="triangle"></div>
                                   <div className="triangle"></div>
                                   <div className="line"></div>
                              </div>

                              <section className="content-block">
                                   <h3>Engagement</h3>
                                   <p> This problem has strong potential to inspire useful product ideas, better workflows, and practical community-driven solutions. </p>
                                   <p>This problem currently has <strong>{likes || 0}</strong> likes and <strong>{comments || 0}</strong> comments.</p>
                                   <p> Users can show interest, discuss the challenge, and propose solution ideas around this concept. </p>
                              </section>
                         </article>

                         <div className="detail-footer">
                              <div className="cta-box">
                                   <h2>Join the discussion?</h2>
                                   <p>
                                        Share your thoughts, feedback, or ideas on this problem.
                                   </p>
                                   <button type='button'
                                        onClick={() => {
                                             setShowComments(true);
                                        }}
                                        className="main-cta" > Write a Comment </button>
                              </div>
                         </div>
                         <CommentsList id={id} />

                    </div>

                    <CommentsOffcanvas
                         show={showComments}
                         handleClose={() => setShowComments(false)}
                         problemId={id}
                    />

                    <EditProblemOffcanvas
                         show={showEdit}
                         handleClose={() => setShowEdit(false)}
                         problemData={problemData}
                         onUpdated={(updated) => setProblemData(prev => ({ ...prev, ...updated }))}
                    />
               </div>

          </>

     );
};

export default InterestedDetail;